import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiJson, apiSend } from "@/api/client";
import { invoiceKeys } from "@/api/invoices";
import type { MessageBrief } from "@/api/mail";
import { segment, withQuery } from "@/api/query";

/*
 * Mirrors api/src/pbo_invoicing/operations/. A call's stage and its deadlines are worked out
 * from what was recorded, so nothing here sets a status: we log what happened.
 */

export type EventKind =
  | "documents_received"
  | "schedule_received"
  | "load_list_received"
  | "planner_assigned"
  | "preliminary_plan_sent"
  | "revision_requested"
  | "revision_sent"
  | "plan_approved"
  | "movins_sent"
  | "emergency_requested"
  | "emergency_acknowledged"
  | "emergency_started"
  | "waiting_started"
  | "waiting_ended"
  | "arrival_reported"
  | "departure_reported"
  | "note";

export type Stage =
  | "awaiting_information"
  | "ready_to_plan"
  | "planning"
  | "plan_sent"
  | "revising"
  | "approved"
  | "distributed"
  | "departed";

export type CallEvent = {
  id: string;
  vessel_call_id: string;
  kind: EventKind;
  label: string;
  happened_at: string;
  note: string;
  created_by_username: string | null;
  version: number;
  created_at: string;
};

export type Target = {
  name: string;
  /** The SLA clause this comes from, so a director can check it. */
  clause: string;
  due_at: string | null;
  done_at: string | null;
  state: "met" | "missed" | "due" | "not_started";
  /** "calendar_for_working": the SLA counts working hours; this counts calendar hours. */
  basis: "calendar" | "calendar_for_working";
  waited_minutes: number;
};

export type CallOperations = {
  vessel_call_id: string;
  vessel_name: string;
  imo: string;
  voyage: string;
  client_code: string;
  terminal: string;
  port: string;
  arrival: string;
  departure: string;
  declared_eta: string | null;
  planner: string;
  stage: Stage;
  stage_label: string;
  waiting: boolean;
  revisions: number;
  targets: Target[];
  next_target: Target | null;
  overdue_count: number;
  missed_count: number;
  billable: boolean;
  invoice_id: string | null;
  invoice_no: string | null;
};

/** `mail` is the e-mails filed under the call, oldest first. */
export type CallOperationsDetail = CallOperations & { events: CallEvent[]; mail: MessageBrief[] };

/** What a director logs, in the order a call usually goes. */
export const EVENT_CHOICES: { value: EventKind; label: string }[] = [
  { value: "documents_received", label: "Vessel documents received" },
  { value: "schedule_received", label: "Downline schedule received" },
  { value: "load_list_received", label: "Load list received" },
  { value: "planner_assigned", label: "Planner assigned" },
  { value: "preliminary_plan_sent", label: "Preliminary plan sent" },
  { value: "revision_requested", label: "Revision requested" },
  { value: "revision_sent", label: "Revision sent" },
  { value: "plan_approved", label: "Plan approved" },
  { value: "movins_sent", label: "Approved plan and MOVINS distributed" },
  { value: "emergency_requested", label: "Emergency support requested" },
  { value: "emergency_acknowledged", label: "Emergency support acknowledged" },
  { value: "emergency_started", label: "Emergency support started" },
  { value: "waiting_started", label: "Waiting on client / vessel / terminal" },
  { value: "waiting_ended", label: "Waiting ended" },
  { value: "arrival_reported", label: "Vessel arrived" },
  { value: "departure_reported", label: "Vessel departed" },
  { value: "note", label: "Note" },
];

export const operationKeys = {
  all: ["operations"] as const,
  board: (filters: BoardFilters) => ["operations", "board", filters] as const,
  call: (id: string) => ["operations", "call", id] as const,
};

export type BoardFilters = { clientId?: string; includeDeparted?: boolean };

export function listOperations(filters: BoardFilters): Promise<CallOperations[]> {
  return apiJson<CallOperations[]>(
    withQuery("/operations", {
      client_id: filters.clientId,
      include_departed: filters.includeDeparted ? "true" : undefined,
    }),
  );
}

export function getCallOperations(callId: string): Promise<CallOperationsDetail> {
  return apiJson<CallOperationsDetail>(`/vessel-calls/${segment(callId)}/operations`);
}

export function recordEvent(
  callId: string,
  input: { kind: EventKind; happened_at: string; note: string },
): Promise<CallEvent> {
  return apiSend<CallEvent>(`/vessel-calls/${segment(callId)}/events`, "POST", input);
}

export function correctEvent(
  eventId: string,
  input: { happened_at: string; note: string },
  version: number,
): Promise<CallEvent> {
  return apiSend<CallEvent>(`/vessel-call-events/${segment(eventId)}`, "PUT", input, version);
}

export function deleteEvent(eventId: string, version: number): Promise<void> {
  return apiSend<void>(`/vessel-call-events/${segment(eventId)}`, "DELETE", undefined, version);
}

export function setPlanner(
  callId: string,
  input: { planner: string; declared_eta: string | null },
  version: number,
): Promise<CallOperationsDetail> {
  return apiSend<CallOperationsDetail>(
    `/vessel-calls/${segment(callId)}/planner`,
    "PUT",
    input,
    version,
  );
}

// --------------------------------------------------------------------------- hooks

export function useOperationsBoard(filters: BoardFilters = {}) {
  return useQuery({
    queryKey: operationKeys.board(filters),
    queryFn: () => listOperations(filters),
    // Deadlines are in hours: a board left open should not go stale.
    refetchInterval: 60_000,
  });
}

export function useCallOperations(callId: string) {
  return useQuery({
    queryKey: operationKeys.call(callId),
    queryFn: () => getCallOperations(callId),
  });
}

function useOperationsMutation<TVars, TResult>(run: (vars: TVars) => Promise<TResult>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: operationKeys.all });
      // Whether a call is billable can change with the plan going out.
      void client.invalidateQueries({ queryKey: invoiceKeys.all });
    },
  });
}

export function useRecordEvent(callId: string) {
  return useOperationsMutation((input: { kind: EventKind; happened_at: string; note: string }) =>
    recordEvent(callId, input),
  );
}

export function useCorrectEvent() {
  return useOperationsMutation(
    ({
      id,
      input,
      version,
    }: {
      id: string;
      input: { happened_at: string; note: string };
      version: number;
    }) => correctEvent(id, input, version),
  );
}

export function useDeleteEvent() {
  return useOperationsMutation(({ id, version }: { id: string; version: number }) =>
    deleteEvent(id, version),
  );
}

export function useSetPlanner(callId: string) {
  return useOperationsMutation(
    ({
      input,
      version,
    }: {
      input: { planner: string; declared_eta: string | null };
      version: number;
    }) => setPlanner(callId, input, version),
  );
}
